import React, { useEffect, useRef, useState } from "react";
import CircularProgress from '@mui/material/CircularProgress';
import "../../headerStyle.css";

const PageHeader = ({
  title,
  breadcrumbs,
  actions,

  showSearch = false,
  searchTerm = "",
  onSearch = () => {},
  results = [],
  onSelectResult = () => {},
  isSearching = false,
  placeholder = "Search...",

  loadingResultId = null,

  renderResult,
  getResultKey = (item, i) => item.id || item.name || i,
}) => {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const searchAreaRef = useRef(null);

  // --- NEW: useEffect to manage the global cursor style ---
  useEffect(() => {
    // When a search starts, change the cursor for the whole page
    if (isSearching) {
      document.body.style.cursor = 'wait';
    } else {
      // When the search finishes, reset the cursor
      document.body.style.cursor = 'default';
    }

    // Cleanup function: ensures the cursor is reset if the component unmounts mid-search
    return () => {
      document.body.style.cursor = 'default';
    };
  }, [isSearching]); // This effect runs only when `isSearching` changes


  const handleFocus = () => {
    if (searchTerm?.length >= 2) setDropdownOpen(true);
  };

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (searchAreaRef.current && !searchAreaRef.current.contains(e.target)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (!showSearch) return;
    if (!searchTerm || searchTerm.length < 2 || (results.length === 0 && !isSearching)) {
      setDropdownOpen(false);
    } else {
      setDropdownOpen(true);
    }
  }, [showSearch, searchTerm, results.length, isSearching]);

  return (
    <div className="page-header">
      <div className="header-left">
        {breadcrumbs && <nav className="breadcrumbs">{breadcrumbs}</nav>}
        <h1 className="page-title">{title}</h1>
      </div>

      <div className="header-right">
        {showSearch && (
          <div className="search-area" ref={searchAreaRef}>
            <div className="search-bar-wrapper"> {/* Added wrapper for positioning */}
              <input
                type="text"
                className="search-bar"
                placeholder={placeholder}
                value={searchTerm}
                onChange={(e) => onSearch(e.target.value)}
                onFocus={handleFocus}
              />
              {/* --- NEW: Spinner inside the search bar --- */}
              {isSearching && <CircularProgress size={20} className="search-input-spinner" />}
            </div>

            {dropdownOpen && (
              <div className="search-dropdown">
                {results.length > 0 ? (
                  results.map((item, i) => {
                    const isItemLoading = getResultKey(item, i) === loadingResultId;

                    return (
                      <div
                        key={getResultKey(item, i)}
                        className={`search-dropdown-item ${isItemLoading ? "is-loading" : ""}`}
                        onMouseDown={isItemLoading ? null : (e) => {
                          e.preventDefault();
                          onSelectResult(item);
                        }}
                      >
                        <div className="search-item-content">
                          {renderResult ? (
                            renderResult(item)
                          ) : (
                            <>
                              <strong>{item.name || item.fullName}</strong>
                              {item.email && <div className="search-meta">{item.email}</div>}
                            </>
                          )}
                        </div>
                        {isItemLoading && <CircularProgress size={20} className="search-item-spinner" />}
                      </div>
                    );
                  })
                ) : (
                  // Hide "No results" while searching to prevent flash of content
                  !isSearching && <div className="search-dropdown-item">No results found.</div>
                )}
              </div>
            )}
          </div>
        )}
        {actions && <div className="header-actions">{actions}</div>}
      </div>
    </div>
  );
};

export default PageHeader;
